import { useRef, useState, useCallback, useEffect } from "react";
import { Dialog, Classes } from '@blueprintjs/core';
import { useIdleTimer } from "react-idle-timer";
import { PUT } from "./../../Screens/dataSaver";
import { fetchGeneralConfig } from "./../../utils/generalConfig";
import Cookies from "universal-cookie";
import style from './index.module.scss';

const DEFAULT_SESSION_TIMEOUT_MINUTES = 15;
const MIN_SESSION_BUFFER_TIMEOUT_MINUTES = 2;
const DEFAULT_SESSION_BUFFER_TIMEOUT_MINUTES = MIN_SESSION_BUFFER_TIMEOUT_MINUTES;

export default function IdleTimer() {
    const [showAlert, setShowAlert] = useState(false);
    const sessionTimeoutRef = useRef(null);
    const idleTimeRef = useRef(null);

    const [sessionTimeoutInMinutes, setSessionTimeoutInMinutes] = useState(DEFAULT_SESSION_TIMEOUT_MINUTES);
    const [sessionBufferTimeoutInMinutes, setSessionBufferTimeoutInMinutes] = useState(DEFAULT_SESSION_BUFFER_TIMEOUT_MINUTES);

    useEffect(() => {
        let isMounted = true;

        const syncFromConfig = (force = false) => {
            fetchGeneralConfig({ force })
                .then((config) => {
                    if (!isMounted || !config) return;
                    if (config.sessionTimeoutInMinutes) {
                        setSessionTimeoutInMinutes(config.sessionTimeoutInMinutes);
                    }
                    if (config.sessionBufferTimeoutInMinutes) {
                        setSessionBufferTimeoutInMinutes(Math.max(config.sessionBufferTimeoutInMinutes, MIN_SESSION_BUFFER_TIMEOUT_MINUTES));
                    }
                })
                .catch((error) => {
                    console.error("Failed to fetch general config for idle timer:", error);
                });
        };

        // Initial load, then periodically re-check so changes made on the
        // General Configuration screen take effect without a page refresh.
        syncFromConfig();
        const intervalId = setInterval(() => syncFromConfig(), 60 * 1000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, []);

    const logout = useCallback(async () => {
        clearTimeout(sessionTimeoutRef.current);
        try {
            const cookies = new Cookies();
            const token = cookies.get("user");

            if (!token) {
                setShowAlert(false);
                window.location.href = "/login";
                return;
            }

            const response = await PUT(`logout`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const logouturi = response?.headers?.location || "/login";
            setShowAlert(false);
            cookies.remove("user", { path: "/" });
            cookies.remove("entityId", { path: "/" });
            window.location.href = logouturi;
        } catch (error) {
            console.error("Idle logout error:", error);
            const cookies = new Cookies();
            cookies.remove("user", { path: "/" });
            cookies.remove("entityId", { path: "/" });
            setShowAlert(false);
            window.location.href = "/login";
        }
    }, []);

    const onIdle = useCallback(() => {
        setShowAlert(true);
        sessionTimeoutRef.current = setTimeout(logout, sessionBufferTimeoutInMinutes * 60 * 1000);
    }, [logout, sessionBufferTimeoutInMinutes]);

    const idleTimer = useIdleTimer({
        crossTab: true,
        ref: idleTimeRef,
        timeout: sessionTimeoutInMinutes * 60 * 1000,
        onIdle: onIdle,
    });

    const onContinue = () => {
        setShowAlert(false);
        clearTimeout(sessionTimeoutRef.current);
        idleTimer.reset();
    };

    return (
        <div>
            <div idleTimer={idleTimer}></div>
            <Dialog isOpen={showAlert} className={`${style.cloneDialog} ${style.dialogPaddingBottom}`}>
                <div className={`${Classes.DIALOG_BODY} ${style.deleteEcecutedContractDialogBackground}`}>
                    <div className={style.spaceBetween}>
                        <p className={style.extensionStyle}>Session Inactive</p>
                    </div>
                    <div className={style.extensionBorder}></div>
                    <p className={`${style.deleteDescriptionStyle} ${style.marginTop20}`}>
                        You have been Inactive for {sessionTimeoutInMinutes} minutes.{" "}
                        {sessionBufferTimeoutInMinutes > 0
                            ? `You will be logged out automatically in ${sessionBufferTimeoutInMinutes} minute${sessionBufferTimeoutInMinutes === 1 ? '' : 's'} unless you continue.`
                            : "You will be logged out automatically unless you continue."}
                    </p>
                    <div className={`${style.positionCenter} ${style.marginTop20}`}>
                        <button className={`${style.cloneButtonStyle} ${style.marginLeft20} ${style.cursorPointer}`} onClick={onContinue}>Continue</button>
                        <button className={`${style.cloneButtonStyle} ${style.marginLeft20} ${style.cursorPointer}`} onClick={logout}>Logout</button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
